import type { CSSProperties, ElementType, ReactNode } from 'react';

export type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';

export type FlexJustify =
  | 'start'
  | 'end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly';

export type FlexAlign = 'start' | 'end' | 'center' | 'stretch' | 'baseline';

export interface FlexProps {
  /** Render as `display: inline-flex` instead of `display: flex`. */
  inline?: boolean;
  /** CSS `justify-content`. */
  justify?: FlexJustify;
  /** CSS `align-items`. */
  align?: FlexAlign;
  /** CSS `flex-direction`. Defaults to `row`. */
  direction?: FlexDirection;
  /** Allow items to wrap onto multiple lines. */
  wrap?: boolean;
  /** Gap between items (both axes). Accepts a number (px) or any CSS length. */
  gap?: number | string;
  /** Horizontal gap between items. Overrides `gap` on the x-axis. */
  gapX?: number | string;
  /** Vertical gap between items. Overrides `gap` on the y-axis. */
  gapY?: number | string;
  /** Extra class name(s) applied to the container. */
  className?: string;
  /** Extra inline styles merged with the computed flex styles. */
  style?: CSSProperties;
  /** The HTML element (or component) to render the flex container as. Defaults to `div`. */
  as?: ElementType;
  children?: ReactNode;
}

const justifyMap: Record<FlexJustify, CSSProperties['justifyContent']> = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  'space-between': 'space-between',
  'space-around': 'space-around',
  'space-evenly': 'space-evenly',
};

const alignMap: Record<FlexAlign, CSSProperties['alignItems']> = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  stretch: 'stretch',
  baseline: 'baseline',
};

/**
 * Renders inline styles directly so it works regardless of which CSS/utility
 * classes are available, avoiding one-off `style={{ display: 'flex', ... }}` blocks.
 */
export function Flex({
  as,
  inline = false,
  justify,
  align,
  direction,
  wrap,
  gap,
  gapX,
  gapY,
  className,
  style,
  children,
}: Readonly<FlexProps>) {
  const Component = as ?? 'div';

  const computedStyle: CSSProperties = {
    display: inline ? 'inline-flex' : 'flex',
    flexDirection: direction,
    justifyContent: justify ? justifyMap[justify] : undefined,
    alignItems: align ? alignMap[align] : undefined,
    flexWrap: wrap ? 'wrap' : undefined,
    gap,
    columnGap: gapX,
    rowGap: gapY,
    ...style,
  };

  return (
    <Component className={className} style={computedStyle}>
      {children}
    </Component>
  );
}

export default Flex;
